import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { useQuery } from '@tanstack/react-query';
import {
  autoScheduleShow,
  cascadeShowTimeline,
  detectLineupConflicts,
  type AutoScheduleResult,
  type LineupDanceWithCast,
  type DancerFamilyRef,
} from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import {
  useShowLineup,
  useDances,
  useSetLineupOrder,
  useAddDanceToLineup,
  useRemoveDanceFromLineup,
} from '@/data/productions';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { SortableLineupItem } from '@/components/lineup/SortableLineupItem';
import { CastModal } from '@/components/lineup/CastModal';
import { useRecentLineupRemovals, useRestoreLastRemoval } from '@/data/conflicts';
import { toast } from '@/lib/toast';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

export const Route = createFileRoute('/admin/productions/$id/shows/$showId')({
  component: ShowDetail,
});

function useShow(showId: string) {
  return useQuery({
    queryKey: ['show', showId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shows')
        .select('*')
        .eq('id', showId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useAllCastForShow(danceIds: string[]) {
  return useQuery({
    enabled: danceIds.length > 0,
    // Defensive copy — Array.prototype.sort mutates in place. The current
    // call site passes a fresh array, but the pattern is fragile if it's
    // ever called with a borrowed reference.
    queryKey: ['cast-for-show', [...danceIds].sort().join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cast_assignments')
        .select('dance_id, dancer_id, dancers(id, first_name, last_name, family_id)')
        .in('dance_id', danceIds);
      if (error) throw error;
      return data ?? [];
    },
  });
}

function ShowDetail() {
  const { id: productionId, showId } = Route.useParams();
  const show = useShow(showId);
  const lineup = useShowLineup(showId);
  const dances = useDances(productionId);
  const setOrder = useSetLineupOrder();
  const addToLineup = useAddDanceToLineup();
  const removeFromLineup = useRemoveDanceFromLineup();

  const lineupDanceIds = (lineup.data ?? []).map((l) => l.dance_id);
  const castQuery = useAllCastForShow(lineupDanceIds);

  // Sortable order state — drives the dnd-kit container. Initialized from
  // the server lineup and updated optimistically on drag end.
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  const [autoOrderOpen, setAutoOrderOpen] = useState(false);
  const [autoPreview, setAutoPreview] = useState<AutoScheduleResult | null>(null);
  const [costumeChangeSeconds, setCostumeChangeSeconds] = useState(120);
  const [maxConsecutiveSameStyle, setMaxConsecutiveSameStyle] = useState(2);
  const [pinOpener, setPinOpener] = useState(true);
  const [pinFinale, setPinFinale] = useState(true);
  const [lockCurrentOrder, setLockCurrentOrder] = useState(false);
  useEffect(() => {
    if (lineup.data) {
      setOrderedIds(lineup.data.map((l) => l.dance_id));
    }
  }, [lineup.data]);

  useEffect(() => {
    const settings = show.data as
      | { costume_change_seconds?: number | null; max_consecutive_same_style?: number | null }
      | null
      | undefined;
    if (!settings) return;
    setCostumeChangeSeconds(settings.costume_change_seconds ?? 120);
    setMaxConsecutiveSameStyle(settings.max_consecutive_same_style ?? 2);
  }, [show.data]);

  // Sensors: pointer (mouse, touch) + keyboard with explicit
  // sortableKeyboardCoordinates — non-negotiable per the a11y plan.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Cast lookup
  const castByDance = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of (castQuery.data ?? []) as { dance_id: string; dancer_id: string }[]) {
      const list = m.get(r.dance_id) ?? [];
      list.push(r.dancer_id);
      m.set(r.dance_id, list);
    }
    return m;
  }, [castQuery.data]);

  // Build the cascade input + run the pure function for live preview.
  const dancesById = useMemo(
    () => new Map((dances.data ?? []).map((d) => [d.id, d])),
    [dances.data],
  );
  const cascadeInput = useMemo(() => {
    return orderedIds
      .map((danceId, i) => {
        const d = dancesById.get(danceId);
        if (!d) return null;
        return {
          dance_id: danceId,
          sequence_index: i + 1,
          length_seconds: d.length_seconds,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [orderedIds, dancesById]);

  const cascade = useMemo(() => {
    if (!show.data) return [];
    return cascadeShowTimeline(
      {
        performance_at: show.data.performance_at,
        default_gap_seconds: show.data.default_gap_seconds,
        intermission_after_sequence: show.data.intermission_after_sequence,
        intermission_minutes: show.data.intermission_minutes,
      },
      cascadeInput,
    );
  }, [show.data, cascadeInput]);

  // Dancer family map for sibling-conflict detection. Build from cast query.
  const dancerFamily = useMemo<Map<string, DancerFamilyRef>>(() => {
    const m = new Map<string, DancerFamilyRef>();
    for (const r of (castQuery.data ?? []) as {
      dancer_id: string;
      dancers: { id: string; first_name: string; last_name: string; family_id: string } | null;
    }[]) {
      const d = r.dancers;
      if (!d) continue;
      m.set(d.id, {
        dancer_id: d.id,
        family_id: d.family_id,
        display_name: `${d.first_name} ${d.last_name}`,
      });
    }
    return m;
  }, [castQuery.data]);

  const lineupWithCast: LineupDanceWithCast[] = cascade.map((c) => ({
    dance_id: c.dance_id,
    sequence_index: c.sequence_index,
    computed_start_time: c.computed_start_time,
    computed_end_time: c.computed_end_time,
    cast_dancer_ids: castByDance.get(c.dance_id) ?? [],
  }));

  const conflicts = useMemo(
    () =>
      detectLineupConflicts(lineupWithCast, dancerFamily, {
        quickChangeMinSeconds: costumeChangeSeconds,
      }),
    [lineupWithCast, dancerFamily, costumeChangeSeconds],
  );
  const conflictedDanceIds = useMemo(() => {
    const s = new Set<string>();
    for (const c of conflicts) for (const id of c.dance_ids) s.add(id);
    return s;
  }, [conflicts]);

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedIds, oldIndex, newIndex);
    setOrderedIds(next); // optimistic
    try {
      await setOrder.mutateAsync({
        show_id: showId,
        ordering: next.map((danceId, i) => ({ dance_id: danceId, sequence_index: i + 1 })),
      });
    } catch (err) {
      toast({
        title: "Couldn't save order",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
      // Revert by reloading from server.
      setOrderedIds((lineup.data ?? []).map((l) => l.dance_id));
    }
  }

  const dancesNotInLineup = (dances.data ?? []).filter((d) => !orderedIds.includes(d.id));

  const [castModalDanceId, setCastModalDanceId] = useState<string | null>(null);
  const castModalDance = castModalDanceId ? dancesById.get(castModalDanceId) : null;

  function generateAutoOrderPreview() {
    if (!show.data) return;
    if (orderedIds.length === 0) {
      toast({
        title: 'No lineup to order',
        description: 'Add dances to this show before generating a running order.',
        variant: 'error',
      });
      return;
    }

    const n = orderedIds.length;
    const scheduleDances = orderedIds
      .map((danceId, i) => {
        const d = dancesById.get(danceId);
        if (!d) return null;
        const danceWithOverride = d as { costume_change_seconds_override?: number | null };
        const pinned =
          lockCurrentOrder || (pinOpener && i === 0) || (pinFinale && i === n - 1) ? i + 1 : null;
        return {
          dance_id: d.id,
          style: d.style,
          length_seconds: d.length_seconds,
          cast_dancer_ids: castByDance.get(d.id) ?? [],
          pinned_sequence_index: pinned,
          costume_change_seconds_override:
            danceWithOverride.costume_change_seconds_override ?? null,
        };
      })
      .filter((d): d is NonNullable<typeof d> => d !== null);

    const result = autoScheduleShow(scheduleDances, {
      performance_at: show.data.performance_at,
      default_gap_seconds: show.data.default_gap_seconds,
      intermission_after_sequence: show.data.intermission_after_sequence,
      intermission_minutes: show.data.intermission_minutes,
      costume_change_seconds: costumeChangeSeconds,
      max_consecutive_same_style: maxConsecutiveSameStyle,
      time_budget_ms: 1500,
    });
    setAutoPreview(result);
  }

  async function applyAutoOrderPreview() {
    if (!autoPreview) return;
    const next = [...autoPreview.order]
      .sort((a, b) => a.sequence_index - b.sequence_index)
      .map((o) => o.dance_id);
    setOrderedIds(next);
    try {
      await setOrder.mutateAsync({
        show_id: showId,
        ordering: next.map((danceId, i) => ({ dance_id: danceId, sequence_index: i + 1 })),
      });
      toast({
        title: 'Auto-order applied',
        description: autoPreview.feasible
          ? 'The previewed running order is now saved.'
          : 'The best-effort order was saved with unresolved warnings.',
        variant: 'success',
      });
      setAutoOrderOpen(false);
    } catch (err) {
      toast({
        title: "Couldn't apply auto-order",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
      setOrderedIds((lineup.data ?? []).map((l) => l.dance_id));
    }
  }

  if (show.isLoading) return <p aria-live="polite">Loading…</p>;
  if (!show.data) {
    return (
      <p role="alert" className="text-[var(--color-danger)]">
        Show not found.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      <header>
        <Link
          to="/admin/productions/$id"
          params={{ id: productionId }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Production
        </Link>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1>{show.data.name}</h1>
            <p className="mt-1 text-[var(--color-ink-muted)]">
              {new Date(show.data.performance_at).toLocaleString()}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAutoOrderOpen((open) => !open)}
            >
              Auto-order show
            </Button>
            <UndoButton showId={showId} />
          </div>
        </div>
      </header>

      {autoOrderOpen && (
        <section aria-labelledby="auto-order-heading" className="surface-elevated space-y-5 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 id="auto-order-heading" className="text-[var(--text-xl)]">
                Auto-order show
              </h2>
              <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                Generate a deterministic preview that balances costume-change rest, dancer overlap,
                pinned positions, and style variety. Nothing is saved until you apply the preview.
              </p>
            </div>
            <Button type="button" variant="ghost" onClick={() => setAutoOrderOpen(false)}>
              Cancel
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="auto-costume-change">Costume-change time</Label>
              <Input
                id="auto-costume-change"
                type="number"
                min={0}
                max={1800}
                step={15}
                value={costumeChangeSeconds}
                onChange={(e) => setCostumeChangeSeconds(Number(e.target.value))}
                aria-describedby="auto-costume-change-help"
              />
              <p
                id="auto-costume-change-help"
                className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-xs)]"
              >
                Seconds of rest required between a dancer&apos;s appearances.
              </p>
            </div>
            <div>
              <Label htmlFor="auto-style-cap">Max same style in a row</Label>
              <Input
                id="auto-style-cap"
                type="number"
                min={1}
                max={20}
                value={maxConsecutiveSameStyle}
                onChange={(e) => setMaxConsecutiveSameStyle(Number(e.target.value))}
              />
            </div>
            <label className="flex items-center gap-2 text-[var(--text-sm)]">
              <input
                type="checkbox"
                checked={pinOpener}
                onChange={(e) => setPinOpener(e.target.checked)}
              />
              Keep current opener
            </label>
            <label className="flex items-center gap-2 text-[var(--text-sm)]">
              <input
                type="checkbox"
                checked={pinFinale}
                onChange={(e) => setPinFinale(e.target.checked)}
              />
              Keep current finale
            </label>
          </div>

          <label className="flex items-center gap-2 text-[var(--text-sm)]">
            <input
              type="checkbox"
              checked={lockCurrentOrder}
              onChange={(e) => setLockCurrentOrder(e.target.checked)}
            />
            Lock every current position for a conflict-only preview
          </label>

          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={generateAutoOrderPreview}>
              {autoPreview ? 'Regenerate preview' : 'Generate preview'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!autoPreview || setOrder.isPending}
              onClick={() => void applyAutoOrderPreview()}
            >
              {setOrder.isPending ? 'Applying…' : 'Apply previewed order'}
            </Button>
          </div>

          {autoPreview && (
            <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
              <div className="surface overflow-x-auto p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Badge tone={autoPreview.feasible ? 'success' : 'warn'}>
                    {autoPreview.feasible ? 'No hard conflicts' : 'Best effort'}
                  </Badge>
                  <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    Score {autoPreview.score}
                  </span>
                </div>
                <ol className="space-y-2">
                  {[...autoPreview.order]
                    .sort((a, b) => a.sequence_index - b.sequence_index)
                    .map((item) => {
                      const d = dancesById.get(item.dance_id);
                      return (
                        <li
                          key={item.dance_id}
                          className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] pb-2 last:border-0 last:pb-0"
                        >
                          <div>
                            <span className="font-medium">
                              {item.sequence_index}. {d?.name ?? item.dance_id}
                            </span>
                            <span className="ml-2 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                              {d?.style ?? 'Unknown'} ·{' '}
                              {new Date(item.computed_start_time).toLocaleTimeString([], {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                          <Badge tone="info">
                            {(castByDance.get(item.dance_id) ?? []).length} cast
                          </Badge>
                        </li>
                      );
                    })}
                </ol>
              </div>

              <aside className="surface space-y-3 p-4" aria-labelledby="auto-order-warnings">
                <h3 id="auto-order-warnings" className="font-medium">
                  Warnings & explanation
                </h3>
                <ul className="space-y-2 text-[var(--text-sm)]">
                  {autoPreview.explanation.map((line, i) => (
                    <li key={i}>{line.replace(/^ {2}• /, '')}</li>
                  ))}
                </ul>
                {autoPreview.violations.length > 0 && (
                  <ul className="space-y-2 border-t border-[var(--color-border)] pt-3 text-[var(--text-sm)]">
                    {autoPreview.violations.map((v, i) => (
                      <li key={`${v.kind}-${i}`} className="flex gap-2">
                        <Badge tone={v.severity === 'hard' ? 'danger' : 'warn'}>{v.kind}</Badge>
                        <span>{v.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </aside>
            </div>
          )}
        </section>
      )}

      {conflicts.length > 0 && (
        <aside
          role="alert"
          aria-labelledby="lineup-conflicts-heading"
          className="surface space-y-2 border border-[var(--color-warn)] bg-[var(--color-warn-muted)] p-5"
        >
          <h2 id="lineup-conflicts-heading" className="font-medium text-[var(--text-base)]">
            {conflicts.length} lineup {conflicts.length === 1 ? 'warning' : 'warnings'}
          </h2>
          <ul className="space-y-1.5 text-[var(--text-sm)]">
            {conflicts.map((c, i) => (
              <li key={`${c.kind}-${i}`} className="flex flex-wrap gap-2">
                <Badge
                  tone={
                    c.severity === 'high' ? 'danger' : c.severity === 'medium' ? 'warn' : 'info'
                  }
                >
                  {c.kind}
                </Badge>
                <span>{c.message}</span>
              </li>
            ))}
          </ul>
        </aside>
      )}

      <section aria-labelledby="lineup-heading">
        <h2 id="lineup-heading" className="text-[var(--text-xl)]">
          Running order
        </h2>
        {/* Visually-hidden hint that each drag handle's aria-describedby
            points to. Screen reader users hear the keyboard interaction
            model when they focus the handle. */}
        <span id="dnd-kb-hint" className="sr-only">
          Press Space to pick up, arrow keys to move, Space again to drop.
        </span>

        {orderedIds.length === 0 ? (
          <Empty
            title="No dances in this show yet"
            description="Add a dance from the picker below to start building the running order."
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => void handleDragEnd(event)}
          >
            <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
              <ul className="mt-4 grid gap-2">
                {orderedIds.map((danceId, i) => {
                  const d = dancesById.get(danceId);
                  if (!d) return null;
                  // Lookup by dance_id rather than array index — cascade
                  // may be shorter than orderedIds if a dance was deleted
                  // server-side since page load.
                  const cascadeRow = cascade.find((c) => c.dance_id === danceId);
                  return (
                    <SortableLineupItem
                      key={danceId}
                      id={danceId}
                      dance={d}
                      sequenceIndex={i + 1}
                      computedStartTime={cascadeRow?.computed_start_time ?? null}
                      hasConflict={conflictedDanceIds.has(danceId)}
                      castCount={(castByDance.get(danceId) ?? []).length}
                      onRemove={(id) => {
                        void removeFromLineup
                          .mutateAsync({ show_id: showId, dance_id: id })
                          .catch((e) =>
                            toast({
                              title: 'Remove failed',
                              description: e instanceof Error ? e.message : 'Unknown error',
                              variant: 'error',
                            }),
                          );
                      }}
                      onOpenCast={(id) => setCastModalDanceId(id)}
                    />
                  );
                })}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </section>

      {dancesNotInLineup.length > 0 && (
        <section aria-labelledby="add-dance-heading">
          <h2 id="add-dance-heading" className="text-[var(--text-xl)]">
            Add a dance
          </h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {dancesNotInLineup.map((d) => (
              <li key={d.id} className="surface flex items-center justify-between p-3">
                <span>
                  <span className="font-medium">{d.name}</span>
                  <span className="ml-2 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                    {d.style} · {Math.floor(d.length_seconds / 60)}:
                    {String(d.length_seconds % 60).padStart(2, '0')}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void addToLineup.mutateAsync({ show_id: showId, dance_id: d.id }).catch((e) =>
                      toast({
                        title: 'Add failed',
                        description: e instanceof Error ? e.message : 'Unknown error',
                        variant: 'error',
                      }),
                    );
                  }}
                >
                  Add to show
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <CastModal
        open={castModalDanceId !== null}
        onClose={() => setCastModalDanceId(null)}
        danceId={castModalDanceId}
        danceName={castModalDance?.name}
      />
    </div>
  );
}

function UndoButton({ showId }: { showId: string }) {
  const removals = useRecentLineupRemovals(showId);
  const restore = useRestoreLastRemoval();
  const recent = removals.data?.[0];

  async function handleUndo() {
    try {
      const restored = await restore.mutateAsync(showId);
      if (!restored) {
        toast({
          title: 'Nothing to restore',
          description: 'No recent removals on this show.',
        });
        return;
      }
      toast({
        title: 'Restored',
        description: 'Dance added back to the end of the lineup.',
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: 'Undo failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  if (!recent) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => void handleUndo()}
      disabled={restore.isPending}
      aria-label="Undo last lineup removal"
    >
      {restore.isPending ? 'Restoring…' : '↶ Undo last removal'}
    </Button>
  );
}
